import React, { FC, Fragment, useState } from 'react';
import { IconButton, IconButtonProps } from '@material-ui/core';
import { darken, makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';

import { Events } from 'analytics';
import { ShareDialog } from 'features/sharing';
import { CustomSVG } from 'components';
import { useContent } from 'hooks';
import { useCanShare } from '../hooks';
import { ReactionType, useAddReactionAnalyticsMutation } from 'api';

interface IProps {
  size: 'small' | 'medium' | 'regular';
  inverted?: boolean;
  position?: IconButtonProps['edge'];
  className?: string;
}

const useStyles = makeStyles(theme => ({
  button: {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      color: theme.palette.common.white,
      backgroundColor: darken(theme.palette.primary.main, 0.1),
    },
  },
  hasShared: {
    backgroundColor: theme.palette.grey[600],
  },
  small: {
    padding: theme.spacing(0.5),
  },
  regular: {
    padding: theme.spacing(1),
  },
  inverted: {
    color: theme.palette.primary.main,
    '&:hover': {
      color: darken(theme.palette.primary.main, 0.1),
    },
  },
}));

const ShareButton: FC<IProps> = ({ size, inverted, position, className }) => {
  const classes = useStyles();
  const content = useContent();
  const showShare = useCanShare(content);

  const { content_id } = content;
  const [open, setOpen] = useState(false);

  const { mutate: addReactionAnalytics } = useAddReactionAnalyticsMutation();

  if (!showShare) {
    return null;
  }

  let iconSize = 0;
  switch (size) {
    case 'small':
      iconSize = 16;
      break;
    case 'medium':
      iconSize = 24;
      break;
    case 'regular':
      iconSize = 32;
      break;
  }
  const buttonClasses = classNames(classes[size], {
    [className]: className,
    [classes.button]: !inverted,
    [classes.inverted]: inverted,
    [classes.hasShared]: content.has_requesting_user_shared,
  });

  function onClickButton() {
    handleOpen();

    addReactionAnalytics({
      content_id,
      reaction_type: ReactionType.Share,
    });
  }

  function handleOpen() {
    setOpen(true);

    const searchParams = new URLSearchParams(location.search);
    window?.analytics?.track(Events.ShareInitiated, {
      content_id,
      referral_source: searchParams.get('es_source'),
    });
  }

  function handleClose() {
    setOpen(false);
  }

  return (
    <Fragment>
      <IconButton className={buttonClasses} onClick={onClickButton} edge={position}>
        <CustomSVG name="share" size={iconSize} />
      </IconButton>
      <ShareDialog onClose={handleClose} open={open} />
    </Fragment>
  );
};

export default ShareButton;
